export const DentalArchSelector = ({
    selectedTeeth,
    onToggleTooth,
    readOnly = false,
    toothColors = {}
}: {
    selectedTeeth: string[],
    onToggleTooth?: (id: string) => void,
    readOnly?: boolean,
    toothColors?: Record<string, { color: string, label: string }>
}) => {
    const renderTooth = (id: string, label: string) => {
        const isSelected = selectedTeeth.includes(id);
        // If specific color exists for this tooth, use it. Otherwise default to blue if selected.
        const customColor = toothColors[id]?.color;

        let baseClasses = "w-9 h-11 md:w-10 md:h-12 flex items-center justify-center text-sm md:text-base font-bold rounded-lg border transition-all duration-200 relative shrink-0 ";

        if (customColor) {
            // If we have a custom color (e.g. for specific service), we use it. 
            // We apply it as inline style or utility class if possible.
            // Since colors might be hex, we might need style prop.
        }

        const activeClass = customColor
            ? `text-white shadow-md transform -translate-y-1 border-transparent`
            : 'bg-blue-600 border-blue-400 text-white shadow-[0_0_15px_rgba(37,99,235,0.5)] transform -translate-y-1';

        const inactiveClass = 'bg-surfaceHighlight border-border text-primary hover:bg-border hover:border-zinc-500 hover:text-primary';

        const className = `${baseClasses} ${(isSelected || customColor) ? activeClass : inactiveClass}`;

        return (
            <button
                key={id}
                type="button"
                onClick={() => !readOnly && onToggleTooth && onToggleTooth(id)}
                disabled={readOnly}
                className={className}
                style={customColor ? { backgroundColor: customColor, borderColor: customColor } : {}}
                title={customColor ? toothColors[id].label : `Tooth ${label}`}
            >
                {label}
                {isSelected && <div className="absolute -bottom-1 w-1.5 h-1.5 bg-current rounded-full opacity-50"></div>}
            </button>
        );
    };

    // Calculate unique services for legend
    const uniqueServices = Object.values(toothColors || {}).reduce((acc, curr) => {
        if (!acc.some(s => s.label === curr.label)) {
            acc.push(curr);
        }
        return acc;
    }, [] as { color: string, label: string }[]);

    return (
        <div className="bg-surface p-4 rounded-2xl border border-border flex flex-col items-center gap-4 shadow-inner relative overflow-hidden">
            <div className="absolute inset-0 opacity-5 pointer-events-none bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary via-transparent to-transparent"></div>
            <div className="w-full overflow-x-auto pb-2 scrollbar-hide">
                <div className="min-w-max px-2 md:px-0 mx-auto flex flex-col gap-6">
                    <div className="relative">
                        <div className="text-xs font-bold text-primary uppercase tracking-widest text-center mb-4">Upper Arch</div>
                        <div className="flex gap-4 md:gap-8 pb-4 border-b border-border justify-center">
                            <div className="flex flex-col items-end">
                                <span className="text-xs font-bold text-primary mb-2 mr-1">UR (Q1)</span>
                                <div className="flex gap-1 md:gap-2">
                                    {[18, 17, 16, 15, 14, 13, 12, 11].map(t => renderTooth(t.toString(), t.toString()))}
                                </div>
                            </div>
                            <div className="flex flex-col items-start">
                                <span className="text-xs font-bold text-primary mb-2 ml-1">UL (Q2)</span>
                                <div className="flex gap-1 md:gap-2">
                                    {[21, 22, 23, 24, 25, 26, 27, 28].map(t => renderTooth(t.toString(), t.toString()))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="relative">
                        <div className="flex gap-4 md:gap-8 pt-2 justify-center">
                            <div className="flex flex-col items-end">
                                <div className="flex gap-1 md:gap-2">
                                    {[48, 47, 46, 45, 44, 43, 42, 41].map(t => renderTooth(t.toString(), t.toString()))}
                                </div>
                                <span className="text-xs font-bold text-primary mt-2 mr-1">LR (Q4)</span>
                            </div>
                            <div className="flex flex-col items-start">
                                <div className="flex gap-1 md:gap-2">
                                    {[31, 32, 33, 34, 35, 36, 37, 38].map(t => renderTooth(t.toString(), t.toString()))}
                                </div>
                                <span className="text-xs font-bold text-primary mt-2 ml-1">LL (Q3)</span>
                            </div>
                        </div>
                        <div className="text-xs font-bold text-primary uppercase tracking-widest text-center mt-4">Lower Arch</div>
                    </div>
                </div>
            </div>

            {/* Legend for Service Colors */}
            {uniqueServices.length > 0 && (
                <div className="w-full pt-4 border-t border-border animate-fade-in">
                    <div className="text-[10px] uppercase font-bold text-secondary mb-2 text-center tracking-wider">Service Key</div>
                    <div className="flex flex-wrap gap-3 justify-center">
                        {uniqueServices.map((service, idx) => (
                            <div key={idx} className="flex items-center gap-2 bg-surfaceHighlight px-2 py-1 rounded-md border border-border">
                                <div className="w-3 h-3 rounded-full shadow-sm" style={{ backgroundColor: service.color }}></div>
                                <span className="text-xs font-medium text-primary">{service.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
